import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { DatePickerWithPresets } from "@/components/ui/date-picker-with-presets";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { MultiSelect, Option } from "@/components/ui/multi-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { exportTeenData, exportIrbSurveyData } from "@/lib/api/admin";
import { useGroups } from "@/lib/api/group";
import {
  endOfDay,
  endOfMonth,
  endOfYear,
  format,
  startOfMonth,
  startOfYear,
  subDays,
  subMonths,
  subYears,
} from "date-fns";
import { Download, Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

interface Group {
  id: string;
  name: string;
}

type DatePreset =
  | "last7days"
  | "last30days"
  | "lastMonth"
  | "last3months"
  | "last6months"
  | "lastYear"
  | "yearToDate"
  | "custom";

const DATE_PRESETS: Record<
  DatePreset,
  { label: string; getDates: () => { start: Date; end: Date } }
> = {
  last7days: {
    label: "Last 7 Days",
    getDates: () => ({
      start: subDays(new Date(), 7),
      end: new Date(),
    }),
  },
  last30days: {
    label: "Last 30 Days",
    getDates: () => ({
      start: subDays(new Date(), 30),
      end: new Date(),
    }),
  },
  lastMonth: {
    label: "Last Month",
    getDates: () => {
      const lastMonth = subMonths(new Date(), 1);
      return {
        start: startOfMonth(lastMonth),
        end: endOfMonth(lastMonth),
      };
    },
  },
  last3months: {
    label: "Last 3 Months",
    getDates: () => ({
      start: startOfMonth(subMonths(new Date(), 3)),
      end: endOfMonth(subMonths(new Date(), 1)),
    }),
  },
  last6months: {
    label: "Last 6 Months",
    getDates: () => ({
      start: startOfMonth(subMonths(new Date(), 6)),
      end: endOfMonth(subMonths(new Date(), 1)),
    }),
  },
  lastYear: {
    label: "Last Year",
    getDates: () => ({
      start: startOfYear(subYears(new Date(), 1)),
      end: endOfYear(subYears(new Date(), 1)),
    }),
  },
  yearToDate: {
    label: "Year to Date",
    getDates: () => ({
      start: startOfYear(new Date()),
      end: new Date(),
    }),
  },
  custom: {
    label: "Custom Range",
    getDates: () => ({
      start: subDays(new Date(), 30),
      end: new Date(),
    }),
  },
};

const defaultStartDate = subDays(new Date(), 30);
const defaultEndDate = new Date();

export function ExportDataDialog() {
  const [isOpen, setIsOpen] = useState(false);
  const [startDate, setStartDate] = useState<Date>(defaultStartDate);
  const [endDate, setEndDate] = useState<Date>(defaultEndDate);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [datePreset, setDatePreset] = useState<DatePreset>("custom");
  const [isExporting, setIsExporting] = useState(false);
  const [isExportingIrb, setIsExportingIrb] = useState(false);
  const [exportAllGroups, setExportAllGroups] = useState(false);
  const [userStatus, setUserStatus] = useState<"active" | "inactive" | "all">(
    "all",
  );
  const { data: groups } = useGroups();
  const { toast } = useToast();

  const resetState = useCallback(() => {
    setStartDate(defaultStartDate);
    setEndDate(defaultEndDate);
    setDatePreset("custom");
    setExportAllGroups(false);
    setUserStatus("all");
    setSelectedGroupIds([]);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen, resetState]);

  const handleDatePresetChange = (preset: DatePreset) => {
    setDatePreset(preset);
    if (preset !== "custom") {
      const { start, end } = DATE_PRESETS[preset].getDates();
      setStartDate(start);
      setEndDate(end);
    }
  };

  const handleStartDateChange = (date: Date | undefined) => {
    setStartDate(date ?? defaultStartDate);
  };

  const handleEndDateChange = (date: Date | undefined) => {
    setDatePreset("custom");
    setEndDate(date ?? defaultEndDate);
  };

  const handleExport = async () => {
    if (
      !startDate ||
      !endDate ||
      (!exportAllGroups && selectedGroupIds.length === 0)
    ) {
      toast({
        title: "Missing required fields",
        description:
          "Please select a date range and either specific groups or enable 'All Groups'",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsExporting(true);
      const data = await exportTeenData(
        startDate,
        endOfDay(endDate),
        exportAllGroups ? "ALL" : selectedGroupIds,
        userStatus,
      );

      if (!data || data.length === 0) {
        toast({
          variant: "destructive",
          title: "No data found",
          description: "There is no data available for the selected filters.",
        });
        return;
      }

      // Convert data to CSV
      const headers = [
        "Teen ID",
        "First Name",
        "Last Name",
        "Teen Phone",
        "Current Mentor",
        "Teen Zipcode",
        "Insurance (If Applicable)",
        "Date of Birth",
        "Start Date with Somethings",
        "Start Date",
        "End Date",
        "Total Days",
        "Engagement Status",
        "Satisfaction Rating",
        "Days Teen Engaged With Mentor",
        "Days Mentor Engaged with Teen",
        "Teen Messages Sent",
        "Mentor Messages Sent",
        "Video Calls",
        "Total Call Duration (minutes)",
        "Speed to Care (hours)",
        "Crisis Resources Used",
        "App Opens",
        "PHQ-8 Score",
        "GAD-7 Score",
        "CSSRS Response",
        "Days in Mentorship",
      ];

      const csvRows = [
        headers.join(","),
        ...data.map((teen) =>
          [
            `"${teen.mentee_id}"`,
            `"${teen.first_name}"`,
            `"${teen.last_name}"`,
            `"${teen.mentee_phone}"`,
            `"${teen.current_mentor}"`,
            `"${teen.zipcode || ""}"`,
            `"${teen?.insurance_provider || ""}"`,
            teen.date_of_birth || "N/A",
            teen.start_with_somethings || "N/A",
            teen.start_date || "N/A",
            teen.end_date || "N/A",
            teen.total_days || "0",
            teen.engagement_status || "N/A",
            teen.satisfaction_rating || "N/A",
            teen.teen_communication_days,
            teen.mentor_communication_days,
            teen.teen_messages_count,
            teen.mentor_messages_count,
            teen.video_call_count,
            teen.total_call_duration,
            teen.speed_to_care || "N/A",
            teen.crisis_resource_utilization || "N/A",
            teen.app_open_count || "N/A",
            teen.phq8_score || "N/A",
            teen.gad7_score || "N/A",
            teen.cssrs_response || "N/A",
            teen.days_in_mentorship || "N/A",
          ].join(","),
        ),
      ].join("\n");

      // Create and download CSV file
      const blob = new Blob([csvRows], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `teen-data-export-${format(startDate, "yyyy-MM-dd")}-to-${format(endDate, "yyyy-MM-dd")}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setIsOpen(false);
      toast({
        title: "Export successful",
        description: "Your data has been exported successfully",
      });
    } catch (error) {
      toast({
        title: "Export failed",
        description: "Failed to export data. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportIrbSurvey = async () => {
    try {
      setIsExportingIrb(true);
      const blob = await exportIrbSurveyData();

      // Create and download Excel file
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `irb-survey-data-${format(new Date(), "yyyy-MM-dd")}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: "Export successful",
        description: "IRB survey data has been exported successfully",
      });
    } catch (error) {
      toast({
        title: "Export failed",
        description: "Failed to export IRB survey data. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsExportingIrb(false);
    }
  };

  const handleGroupSelection = async (selectedOptions: Option[]) => {
    setExportAllGroups(false);
    setSelectedGroupIds(selectedOptions.map((option) => option.value));
  };

  const handleAllGroupsChange = (checked: boolean) => {
    setExportAllGroups(checked);
    if (checked) {
      setSelectedGroupIds([]);
    }
  };

  const groupOptions =
    groups?.map((group: Group) => ({
      value: group.id,
      label: group.name,
    })) || [];

  const selectedOptions = selectedGroupIds
    .map((id) => {
      const group = groups?.find((g) => g.id === id);
      return group ? { value: group.id, label: group.name } : null;
    })
    .filter((option): option is Option => option !== null);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2">
          <Download className="h-4 w-4" />
          Export Data
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Export Teen Data</DialogTitle>
          <DialogDescription>
            Export teen data for the selected date range and groups.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="space-y-4">
            {/* Date Selection Section */}
            <Card className="p-4">
              <h3 className="mb-4 text-base font-semibold">Date Range</h3>
              <div className="space-y-4">
                <div>
                  <label className="mb-2 block text-sm font-medium text-muted-foreground">
                    Quick Select
                  </label>
                  <Select
                    value={datePreset}
                    onValueChange={handleDatePresetChange}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select date range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="last7days">Last 7 Days</SelectItem>
                      <SelectItem value="last30days">Last 30 Days</SelectItem>
                      <SelectItem value="lastMonth">Last Month</SelectItem>
                      <SelectItem value="last3months">Last 3 Months</SelectItem>
                      <SelectItem value="last6months">Last 6 Months</SelectItem>
                      <SelectItem value="lastYear">Last Year</SelectItem>
                      <SelectItem value="yearToDate">Year to Date</SelectItem>
                      <SelectItem value="custom">Custom Range</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-muted-foreground">
                      Start Date
                    </label>
                    <DatePickerWithPresets
                      date={startDate}
                      onDateChange={handleStartDateChange}
                      className="w-full"
                      showPresets={false}
                      disablePastDates={false}
                      modal={true}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-muted-foreground">
                      End Date
                    </label>
                    <DatePickerWithPresets
                      date={endDate}
                      onDateChange={handleEndDateChange}
                      className="w-full"
                      showPresets={false}
                      disablePastDates={false}
                      modal={true}
                    />
                  </div>
                </div>
              </div>
            </Card>

            {/* Filters Section */}
            <Card className="p-4">
              <h3 className="mb-4 text-base font-semibold">Filters</h3>
              <div className="space-y-4">
                {/* User Status */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-muted-foreground">
                    User Status
                  </label>
                  <Select
                    value={userStatus}
                    onValueChange={(value: "active" | "inactive" | "all") =>
                      setUserStatus(value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select user status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Users</SelectItem>
                      <SelectItem value="active">Active Users</SelectItem>
                      <SelectItem value="inactive">Inactive Users</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Groups Selection */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-muted-foreground">
                    Groups
                  </label>
                  <div className="mb-2 flex items-center space-x-2">
                    <Checkbox
                      id="allGroups"
                      checked={exportAllGroups}
                      onCheckedChange={handleAllGroupsChange}
                    />
                    <label
                      htmlFor="allGroups"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      Export data for all groups
                    </label>
                  </div>

                  {!exportAllGroups && (
                    <MultiSelect
                      options={groupOptions}
                      defaultValue={selectedOptions}
                      onChange={handleGroupSelection}
                      className="w-full"
                      placeholder="Select specific groups..."
                    />
                  )}
                </div>
              </div>
            </Card>
          </div>
        </div>

        <DialogFooter className="flex flex-col items-center justify-end space-y-2 sm:flex-row sm:space-x-2 sm:space-y-0">
          <Button
            variant="outline"
            onClick={handleExportIrbSurvey}
            disabled={isExportingIrb}
            className="w-full sm:w-auto"
          >
            {isExportingIrb ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Exporting IRB Data...
              </>
            ) : (
              "Export IRB Survey Data"
            )}
          </Button>
          <div className="flex flex-col space-y-2 sm:flex-row sm:space-x-2 sm:space-y-0">
            <Button
              variant="outline"
              onClick={() => setIsOpen(false)}
              disabled={isExporting || isExportingIrb}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              onClick={handleExport}
              disabled={isExporting || isExportingIrb}
              className="w-full sm:w-auto"
            >
              {isExporting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Exporting...
                </>
              ) : (
                "Export Teen Data"
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
